import React from "react";
import {
    Backpack,
    Package,
    Pickaxe,
    Crosshair,
    Rocket,
    Crown,
} from "lucide-react";
import ScrollReveal from "./ScrollReveal";
import { useI18n } from "@/i18n/I18nProvider";

/**
 * priceRegular = preço de lista; pricePromo = valor em promoção (mostrado em destaque).
 * Nome, descrição e itens vêm de i18n: kits.<id>.name / .desc / .items.
 */
const KITS = [
    { id: "start", priceRegular: "R$ 15,90", pricePromo: "R$ 7,90", Icon: Backpack },
    { id: "farm", priceRegular: "R$ 20,90", pricePromo: "R$ 10,90", Icon: Pickaxe },
    { id: "pvp", priceRegular: "R$ 44,90", pricePromo: "R$ 24,90", Icon: Crosshair, popular: true },
    { id: "base", priceRegular: "R$ 50,90", pricePromo: "R$ 30,90", Icon: Package },
    { id: "raid", priceRegular: "R$ 60,90", pricePromo: "R$ 39,90", Icon: Rocket },
    {
        id: "boss",
        priceRegular: "R$ 100,90",
        pricePromo: "R$ 59,90",
        Icon: Crown,
        highlightKey: "kits.badgeComplete",
    },
];

function KitCard({ k, index, purchaseDisabled }) {
    const { Icon } = k;
    const { t } = useI18n();
    const items = t(`kits.${k.id}.items`);
    return (
        <ScrollReveal delay={index * 90}>
            <div
                data-testid={`kit-card-${k.id}`}
                className={`relative h-full rounded-2xl p-6 border flex flex-col gold-border-hover ${
                    k.highlightKey ? "border-gold/40" : "border-white/10"
                }`}
                style={{
                    background:
                        "linear-gradient(180deg, rgba(26,15,10,0.9), rgba(10,7,6,0.85))",
                }}
            >
                {k.highlightKey && (
                    <span
                        className="absolute top-4 right-4 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.2em] font-bold"
                        style={{ background: "rgba(255,184,0,0.15)", color: "#FFB800", border: "1px solid rgba(255,184,0,0.35)" }}
                    >
                        {t(k.highlightKey)}
                    </span>
                )}

                {/* Icon display */}
                <div
                    className="mb-5 w-14 h-14 rounded-xl flex items-center justify-center border"
                    style={{
                        background: "rgba(255,184,0,0.06)",
                        borderColor: "rgba(255,184,0,0.2)",
                        color: "#FFB800",
                    }}
                >
                    <Icon size={34} strokeWidth={1.65} aria-hidden />
                </div>

                <div className="mb-4">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-zrs-metal mb-1">
                        {t("kits.oneTime")}
                    </div>
                    <h3 className="text-xl font-bold text-zrs-offwhite">{t(`kits.${k.id}.name`)}</h3>
                    <p className="text-[13px] text-zrs-metal mt-1">{t(`kits.${k.id}.desc`)}</p>
                </div>

                <ul className="space-y-1.5 mb-6 flex-1">
                    {(Array.isArray(items) ? items : []).map((it, i) => (
                        <li
                            key={i}
                            className="flex items-start gap-2 text-[13px] text-zrs-offwhite/75"
                        >
                            <span className="mt-[7px] w-1 h-1 rounded-full bg-gold/70 shrink-0" />
                            {it}
                        </li>
                    ))}
                </ul>

                <div className="flex flex-wrap items-end justify-between gap-3 pt-4 border-t border-white/5">
                    <div data-testid={`kit-${k.id}-price`} className="min-w-0">
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            <span className="text-[13px] text-zrs-metal line-through decoration-zrs-metal/60">
                                {k.priceRegular}
                            </span>
                            <span className="text-[10px] uppercase tracking-[0.15em] text-gold/80">
                                {t("kits.promo")}
                            </span>
                        </div>
                        <div className="text-xl font-bold text-gold md:text-2xl leading-tight mt-0.5">
                            {k.pricePromo}
                        </div>
                    </div>
                    <button
                        type="button"
                        disabled={purchaseDisabled}
                        aria-disabled={purchaseDisabled}
                        data-testid={`kit-${k.id}-buy-btn`}
                        className={`btn-shimmer shrink-0 px-4 py-2 rounded-full font-bold text-[12px] tracking-wide transition-transform ${
                            purchaseDisabled
                                ? "cursor-not-allowed opacity-50"
                                : "hover:scale-[1.03]"
                        }`}
                        style={{ background: "#FFB800", color: "#050505" }}
                    >
                        {t("kits.buy")}
                    </button>
                </div>
            </div>
        </ScrollReveal>
    );
}

export default function KitsSection() {
    const { t } = useI18n();
    return (
        <section
            id="kits"
            data-testid="kits-section"
            className="relative py-24 md:py-32 px-6 md:px-8"
        >
            <div className="max-w-6xl mx-auto">
                <ScrollReveal>
                    <div className="mb-14 md:mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.4em] text-gold/80 mb-3">
                                {t("kits.eyebrow")}
                            </div>
                            <div className="flex flex-wrap items-center gap-3 gap-y-2">
                                <h2 className="text-3xl md:text-5xl font-bold text-zrs-offwhite">
                                    {t("kits.title")}
                                </h2>
                                <span
                                    className="inline-flex shrink-0 items-center rounded-full border border-white/15 bg-white/[0.06] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-zrs-offwhite/70"
                                    data-testid="kits-dev-badge"
                                >
                                    {t("kits.devBadge")}
                                </span>
                            </div>
                        </div>
                        <p className="md:max-w-md text-zrs-offwhite/60 text-[14px]">
                            {t("kits.intro")}
                        </p>
                    </div>
                </ScrollReveal>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                    {KITS.map((k, i) => (
                        <KitCard
                            key={k.id}
                            k={k}
                            index={i}
                            purchaseDisabled
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
